import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTypedSearchParams } from "react-router-typesafe-routes";
import { SortingState } from "@tanstack/react-table";
import {
  TenantedClientApplicationManagedByChoices,
  TenantedClientApplicationReviewStateChoices,
  TenantedClientApplicationSupportedByChoices,
} from "@graphql/generated/graphql.g";

import { TableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/TableColumnSettingsProvider";

import {
  ClientApplicationFilterForm,
  ClientApplicationFilterFormRef,
  ClientApplicationFilterFormValues,
} from "@components/Forms/ClientApplicationFilterForm/ClientApplicationFilterForm";
import { ClientApplicationsTable } from "@components/Tables";
import {
  areFiltersActive,
  getDefaultFilterValues,
} from "@components/Tables/ClientApplicationsTable/ClientApplicationsTableConfig";
import { ClientApplicationsTableColumnCustomization } from "@components/Tables/ClientApplicationsTable/UI/ClientApplicationsTableColumnCustomization";
import {
  ClientApplicationTableColumns,
  DEFAULT_CLIENT_APPLICATION_TABLE_COLUMN_ORDER,
  DEFAULT_CLIENT_APPLICATION_TABLE_COLUMN_SETTINGS,
} from "@components/Tables/ClientApplicationsTable/UI/ClientApplicationsTableColumns";
import { useClientApplicationTableExport } from "@components/Tables/ClientApplicationsTable/utils/useClientApplicationTableExport";

import { Box } from "@design-system/Box/Box";
import { IconButton } from "@design-system/IconButton/IconButton";
import { SlideInPanel } from "@design-system/SlideInPanel";
import { Text } from "@design-system/Text/Text";

import { PortalHeaderActions } from "@layouts/PortalPage/PortalHeaderActions";

import { routes } from "@pages/routes";

import {
  sortingFromSearchParams,
  sortingToSearchParams,
} from "@utils/table/tableSearchParams";

type Props = {
  clientId: string;
};

const DEFAULT_SORTING: SortingState = [
  { id: ClientApplicationTableColumns.productName, desc: false },
];

const applicationsRoute = routes.portal.clients.client.applications;

const toFilterValues = (
  searchParams: ReturnType<typeof applicationsRoute.$deserializeSearchParams>,
): ClientApplicationFilterFormValues => ({
  parentCategoryId: searchParams.parentCategoryId,
  productNameSearch: searchParams.productNameSearch,
  businessOwnerId: searchParams.businessOwnerId,
  createdAt_Gte: searchParams.createdAt_Gte,
  createdAt_Lte: searchParams.createdAt_Lte,
  reviewState: searchParams.reviewState.length
    ? (searchParams.reviewState as TenantedClientApplicationReviewStateChoices[])
    : undefined,
  managedBy: searchParams.managedBy.length
    ? (searchParams.managedBy as TenantedClientApplicationManagedByChoices[])
    : undefined,
  supportedBy: searchParams.supportedBy.length
    ? (searchParams.supportedBy as TenantedClientApplicationSupportedByChoices[])
    : undefined,
  isVisibleFilter:
    searchParams.isVisibleFilter === "all" ||
    searchParams.isVisibleFilter === "hidden"
      ? searchParams.isVisibleFilter
      : "visible",
});

export const ClientApplicationsCard = ({ clientId }: Props) => {
  const [searchParams, setSearchParams] = useTypedSearchParams(applicationsRoute);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false);
  const filterValues = useMemo(
    () => toFilterValues(searchParams),
    [searchParams],
  );
  const sorting = useMemo(
    () =>
      sortingFromSearchParams(
        searchParams.sort,
        searchParams.dir,
        DEFAULT_SORTING,
      ),
    [searchParams.sort, searchParams.dir],
  );

  const filterFormRef = useRef<ClientApplicationFilterFormRef>(null);

  const syncToUrl = useCallback(
    (values: ClientApplicationFilterFormValues, nextSorting: SortingState) => {
      const { sort, dir } = sortingToSearchParams(nextSorting);
      setSearchParams(
        {
          parentCategoryId: values.parentCategoryId,
          productNameSearch: values.productNameSearch,
          businessOwnerId: values.businessOwnerId,
          createdAt_Gte: values.createdAt_Gte,
          createdAt_Lte: values.createdAt_Lte,
          reviewState: values.reviewState ?? [],
          managedBy: values.managedBy ?? [],
          supportedBy: values.supportedBy ?? [],
          isVisibleFilter:
            values.isVisibleFilter && values.isVisibleFilter !== "visible"
              ? values.isVisibleFilter
              : undefined,
          sort,
          dir,
        },
        { untypedSearchParams: true },
      );
    },
    [setSearchParams],
  );

  const filtersActive = useMemo(() => {
    return areFiltersActive(filterValues);
  }, [filterValues]);

  const handleFilterClick = useCallback(() => {
    setIsFilterPanelOpen(true);
  }, []);

  const handleCloseFilterPanel = useCallback(() => {
    setIsFilterPanelOpen(false);
  }, []);

  const handleFiltersReset = useCallback(() => {
    syncToUrl(getDefaultFilterValues(), sorting);
  }, [syncToUrl, sorting]);

  const handleFilterChange = useCallback(
    (newValues: ClientApplicationFilterFormValues) => {
      syncToUrl(newValues, sorting);
      setIsFilterPanelOpen(false);
    },
    [syncToUrl, sorting],
  );

  const handleApplyFilters = useCallback(() => {
    if (filterFormRef.current) {
      syncToUrl(filterFormRef.current.getCurrentValues(), sorting);
      setIsFilterPanelOpen(false);
    }
  }, [syncToUrl, sorting]);

  const handleSortingChange = useCallback(
    (nextSorting: SortingState) => {
      syncToUrl(filterValues, nextSorting);
    },
    [syncToUrl, filterValues],
  );

  const handleColumnSettingsClick = useCallback(() => {
    setIsColumnSettingsOpen(true);
  }, []);

  const handleCloseColumnSettings = useCallback(() => {
    setIsColumnSettingsOpen(false);
  }, []);

  const { exportData, isExporting } = useClientApplicationTableExport({
    tableId: "client-applications-table",
    clientId,
    exportType: "csv",
    reviewState: filterValues.reviewState,
    managedBy: filterValues.managedBy,
    supportedBy: filterValues.supportedBy,
    businessOwnerId: filterValues.businessOwnerId,
    productNameSearch: filterValues.productNameSearch,
    parentCategoryId: filterValues.parentCategoryId,
    createdAt_Gte: filterValues.createdAt_Gte,
    createdAt_Lte: filterValues.createdAt_Lte,
    isVisibleFilter: filterValues.isVisibleFilter,
  });

  const handleExportToCsv = useCallback(async () => {
    try {
      await exportData();
      toast.success("Client applications exported successfully!");
    } catch (error) {
      toast.error("Failed to export client applications");
      console.error("Export error:", error);
    }
  }, [exportData]);

  return (
    <TableColumnSettingsProvider
      tableId="client-applications-table"
      defaultColumnSettings={DEFAULT_CLIENT_APPLICATION_TABLE_COLUMN_SETTINGS}
      defaultColumnOrder={DEFAULT_CLIENT_APPLICATION_TABLE_COLUMN_ORDER}
    >
      <PortalHeaderActions>
        <Box direction="row" gap={8} align="center">
          {filtersActive && (
            <Text size={12} color={"textSecondary"}>
              Filters are applied
            </Text>
          )}
          <IconButton
            size="sm"
            icon="faFilter"
            onClick={handleFilterClick}
            outline={"sm"}
            showBubble={filtersActive}
            tooltip="Show filters"
          />
          <IconButton
            size="sm"
            icon="faTableColumns"
            onClick={handleColumnSettingsClick}
            outline={"sm"}
            tooltip="Show/Hide columns"
          />
          <IconButton
            size="sm"
            icon={isExporting ? "faArrowsSpin" : "faFileCsv"}
            onClick={handleExportToCsv}
            outline={"sm"}
            tooltip={
              isExporting ? "Exporting..." : "Export table data to CSV"
            }
            disabled={isExporting}
          />
        </Box>
      </PortalHeaderActions>
      <ClientApplicationsTable
        clientId={clientId}
        reviewState={
          filterValues.reviewState && filterValues.reviewState.length > 0
            ? filterValues.reviewState
            : undefined
        }
        managedBy={
          filterValues.managedBy && filterValues.managedBy.length > 0
            ? filterValues.managedBy
            : undefined
        }
        supportedBy={
          filterValues.supportedBy && filterValues.supportedBy.length > 0
            ? filterValues.supportedBy
            : undefined
        }
        businessOwnerId={filterValues.businessOwnerId}
        productNameSearch={filterValues.productNameSearch}
        parentCategoryId={filterValues.parentCategoryId}
        createdAt_Gte={filterValues.createdAt_Gte}
        createdAt_Lte={filterValues.createdAt_Lte}
        isVisibleFilter={filterValues.isVisibleFilter}
        sorting={sorting}
        onSortingChange={handleSortingChange}
      />

      <SlideInPanel
        isOpen={isFilterPanelOpen}
        onClose={handleCloseFilterPanel}
        onCancel={handleFiltersReset}
        onApply={handleApplyFilters}
        cancelButtonText={filtersActive ? "Clear filters" : undefined}
        applyButtonText="Apply filters"
        title="Application Filters"
      >
        <ClientApplicationFilterForm
          ref={filterFormRef}
          onFilterChange={handleFilterChange}
          values={filterValues}
          clientId={clientId}
          errorsAPI={undefined}
        />
      </SlideInPanel>

      <ClientApplicationsTableColumnCustomization
        isOpen={isColumnSettingsOpen}
        onClose={handleCloseColumnSettings}
      />
    </TableColumnSettingsProvider>
  );
};
